"use client";

import React, { useState } from "react";
import {
  Close,
  Minimize,
  Fullscreen,
  FullscreenExit,
  DragIndicator,
} from "@mui/icons-material";
import { useSOLAFloating } from "./context";
import { ChatHeader } from "./ChatHeader";
import { ChatInterface } from "./ChatInterface";
import Image from "next/image";
import { colors, spacing, radius, shadows, textStyles } from "@/ui/tokens";
import styles from "./FloatingWindow.module.css";

export function FloatingWindow({
  onClose,
  onMinimize,
  onMaximize,
  onResize,
  isMaximized,
  isDragging,
}) {
  const { state } = useSOLAFloating();
  const [isResizing, setIsResizing] = useState(false);

  // New chat handler provided by ChatInterface (clears both useChat and store)
  const newChatHandlerRef = React.useRef(null);

  const handleNewChat = () => {
    if (newChatHandlerRef.current) {
      newChatHandlerRef.current();
    }
  };

  const handleNewChatReady = React.useCallback((handler) => {
    newChatHandlerRef.current = handler;
  }, []);

  // Handle resize (horizontal and vertical)
  const handleResizeMouseDown = (e, direction) => {
    if (isMaximized) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = state.size.width;
    const startHeight = state.size.height;

    const handleMouseMove = (e) => {
      const updates = {};
      const maxWidth = Math.min(1200, window.innerWidth - 20);
      const maxHeight = Math.min(900, window.innerHeight - 80);

      if (direction.includes("left")) {
        const newWidth = Math.max(
          400,
          Math.min(maxWidth, startWidth - (e.clientX - startX))
        );
        updates.width = newWidth;
      }

      if (direction.includes("top")) {
        const newHeight = Math.max(
          400,
          Math.min(maxHeight, startHeight - (e.clientY - startY))
        );
        updates.height = newHeight;
      }

      if (Object.keys(updates).length > 0) {
        onResize(updates);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const headerHeight = 56;
  const chatHeaderHeight = 48;
  const contentHeight = state.size.height - headerHeight - chatHeaderHeight;
  const unreadCount = state.notifications.filter((n) => !n.read).length;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: isMaximized ? 0 : radius.md,
        overflow: "hidden",
        backgroundColor: colors.canvas.default,
        boxShadow: isMaximized ? "none" : shadows[4],
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Window Header */}
      <div
        style={{
          height: headerHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          backgroundColor: colors.action.product,
          color: colors.text.inverse,
          cursor: "default",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing[2],
            flex: 1,
            minWidth: 0,
          }}
        >
          <Image
            src="/img/floats/sola.svg"
            alt="SOLA"
            width={32}
            height={32}
            style={{ display: "block" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: spacing[3] }}>
            <span
              style={{
                ...textStyles.subtitle1,
                fontWeight: 600,
                lineHeight: 1,
                color: "inherit",
              }}
            >
              SOLA AI Assistant
            </span>
            <span
              style={{
                paddingLeft: 6,
                paddingRight: 6,
                paddingTop: 2,
                paddingBottom: 2,
                borderRadius: radius.sm,
                border: "1px solid rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.05em",
                color: "white",
                lineHeight: 1,
              }}
            >
              BETA
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            className={styles.windowButton}
            onClick={onMinimize}
            aria-label="Minimize"
          >
            <Minimize style={{ fontSize: 16 }} />
          </button>

          <button
            className={styles.windowButton}
            onClick={onMaximize}
            aria-label={isMaximized ? "Restore" : "Maximize"}
          >
            {isMaximized ? (
              <FullscreenExit style={{ fontSize: 16 }} />
            ) : (
              <Fullscreen style={{ fontSize: 16 }} />
            )}
          </button>

          <button
            className={`${styles.windowButton} ${styles.windowButtonClose}`}
            onClick={onClose}
            aria-label="Close"
          >
            <Close style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* Chat Header with New Chat button */}
      <ChatHeader onNewChat={handleNewChat} />

      {/* Content Area */}
      <div
        style={{
          flex: 1,
          height: contentHeight,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <ChatInterface onNewChatReady={handleNewChatReady} />
          </div>
        </div>

        {/* Resize Handles */}
        {!isMaximized && (
          <>
            <div
              className={styles.resizeHandleLeft}
              onMouseDown={(e) => handleResizeMouseDown(e, "left")}
            >
              <DragIndicator
                className={styles.resizeIcon}
                style={{ fontSize: 18, transform: "rotate(90deg)" }}
              />
            </div>

            <div
              className={styles.resizeHandleTop}
              onMouseDown={(e) => handleResizeMouseDown(e, "top")}
            >
              <DragIndicator
                className={styles.resizeIcon}
                style={{ fontSize: 18 }}
              />
            </div>

            <div
              className={styles.resizeHandleCorner}
              onMouseDown={(e) => handleResizeMouseDown(e, "left-top")}
              style={{ opacity: isResizing ? 0.9 : undefined }}
            >
              <DragIndicator
                style={{
                  fontSize: 16,
                  color: "white",
                  transform: "rotate(45deg)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Status Bar */}
      <div
        style={{
          height: 24,
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: colors.canvas.card,
          ...textStyles.caption,
          color: colors.text.muted,
          borderTop: `1px solid ${colors.border.subtle}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          {unreadCount > 0 && (
            <span style={{ color: colors.signal.warning }}>
              {unreadCount} notifications
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          {state.preferences.keyboardShortcuts && (
            <span style={{ fontFamily: "monospace" }}>
              Ctrl+Shift+S
            </span>
          )}
          <div className={styles.statusDot} />
        </div>
      </div>
    </div>
  );
}
